/**
 * MusicArtistsPage - 音楽ライブラリの「アーティスト」一覧（Apple Music 相当）
 *
 * - アーティストは author / narrator タグで表現され、専用の画像を持たないため
 *   代表アルバムのアートワークを円形サムネイルとして使う
 * - 名前順ソート・頭文字インデックス・検索はクライアント側で行う
 */

import { ChevronRight, MicVocal } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { getMusicArtists } from '@/api/api'
import type { MusicArtistItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import { AuthImage } from '@/components/ui/auth-image'
import { SearchBox } from '@/components/ui/search-box'
import { Spinner } from '@/components/ui/spinner'
import { PATHS } from '@/routes/paths'

import { DOWNLOADED_ONLY_PARAM, DOWNLOADED_ONLY_QUERY } from './constants'
import { IndexedList } from './indexed-list'
import { buildOfflineArtists, useOfflineMusicTracks } from './offline-music'

const ARTIST_ROW_HEIGHT = 72

const getArtistKey = (artist: MusicArtistItem) => `artist-${artist.tag_id}`
const getArtistName = (artist: MusicArtistItem) => artist.name

const renderArtistRow = (artist: MusicArtistItem, onClick: () => void) => (
  <button type="button" onClick={onClick} className="flex h-full w-full items-center gap-3 border-none pl-4 text-left">
    <span className="size-14 shrink-0 overflow-hidden rounded-full bg-white/10">
      {artist.thumbnail ? (
        <AuthImage
          src={artist.thumbnail}
          alt={artist.name}
          className="size-full object-cover"
          cacheKey={`music-artist-${artist.tag_id}`}
        />
      ) : (
        <span className="flex size-full items-center justify-center">
          <MicVocal size={20} className="text-muted-foreground" />
        </span>
      )}
    </span>
    <span className="flex h-full min-w-0 flex-1 items-center gap-2 border-b border-white/10 pr-4">
      <span className="min-w-0 flex-1 line-clamp-2">{artist.name}</span>
      <ChevronRight size={20} className="shrink-0 text-muted-foreground" />
    </span>
  </button>
)

export const MusicArtistsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const offlineTracks = useOfflineMusicTracks()

  // ダウンロード済みライブラリからの遷移では、オフライン保存済みの曲を持つアーティストだけを表示する
  const isDownloadedOnly = searchParams.get(DOWNLOADED_ONLY_PARAM) === '1'

  const [artists, setArtists] = useState<MusicArtistItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    // ダウンロード済み表示は IndexedDB だけで組み立てられるためサーバーに問い合わせない
    if (isDownloadedOnly) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    getMusicArtists()
      .then((res) => {
        if (cancelled) return
        setArtists(res.artists)
      })
      .catch(() => {
        if (cancelled) return
        setError('アーティストの取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isDownloadedOnly])

  const downloadedArtists = useMemo(
    () => (offlineTracks === null ? null : buildOfflineArtists(offlineTracks)),
    [offlineTracks],
  )

  const filteredArtists = useMemo(() => {
    const base = isDownloadedOnly ? (downloadedArtists ?? []) : artists
    const keyword = query.trim().toLowerCase()
    if (!keyword) return base
    return base.filter((a) => a.name.toLowerCase().includes(keyword))
  }, [artists, query, isDownloadedOnly, downloadedArtists])

  const isPending = isDownloadedOnly ? downloadedArtists === null : isLoading

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header
        title="アーティスト"
        onClose={() => navigate(isDownloadedOnly ? PATHS.MUSIC.DOWNLOADED : PATHS.MUSIC.LIBRARY)}
      />

      <div className="flex shrink-0 px-4 py-3">
        <SearchBox value={query} onChange={(e) => setQuery(e.target.value)} placeholder="検索" />
      </div>

      {isPending && (
        <div className="flex flex-1 justify-center py-12">
          <Spinner size={40} />
        </div>
      )}
      {!isPending && error && <div className="flex-1 py-12 text-center text-muted-foreground">{error}</div>}
      {!isPending && !error && filteredArtists.length === 0 && (
        <div className="flex-1 py-12 text-center text-muted-foreground">アーティストが見つかりません</div>
      )}
      {!isPending && !error && filteredArtists.length > 0 && (
        <IndexedList
          items={filteredArtists}
          getKey={getArtistKey}
          getName={getArtistName}
          rowHeight={ARTIST_ROW_HEIGHT}
          renderRow={(artist) =>
            renderArtistRow(artist, () => {
              const path = PATHS.MUSIC.ARTIST_DETAIL.replace(':tag_id', String(artist.tag_id))
              navigate(isDownloadedOnly ? `${path}?${DOWNLOADED_ONLY_QUERY}` : path)
            })
          }
        />
      )}
    </div>
  )
}
